import {
  keepPreviousData,
  useMutation,
  useQueries,
  useQuery
} from "@tanstack/react-query";
import { instance } from "api/apiBase";
import addressApi from "api/landlord/address";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { ApplicationStatus, PaginatedResponse } from "shared/types";
import { useSearchParamsState } from "shared/hooks/use-search-params-state";
import {
  Button,
  Input,
  InputSize,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  Show,
  IconButtonVariant
} from "shared/components";
import { FilterLines, Plus, XClose } from "untitledui-js-base";
import { TableApplications } from "./components/Table";
import { Pagination } from "shared/components/Pagination";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";
import { Application } from "types/application";
import { RowSelectionState, SortingState } from "@tanstack/react-table";
import { MergeApplicantsModal } from "./components/MergeApplicantsModal";
import { CancelOrRefundModal } from "../CancelOrRefundModal";
import queryString from "qs";
import { MoveModal } from "../MoveModal";
import { FiltersDrawer } from "./components/FiltersDrawers";
import { FilterBadge } from "./components/FiltersDrawers/FilterBadge";
import { Tabs } from "../../components/Tabs";
import { RefundModal } from "../RefundModal";
import { useMedia } from "shared/providers";
import { CountNotification } from "shared/app/ApplicationsTableView/components/CountNotification";
import { InviteAndCreateModal } from "../InviteAndCreateModal";
import { UncancelModal } from "../UncancelModal";
import { ColumnsMenu } from "./components/ColumnsMenu";
import { getErrorMessage } from "utils/error";
import { DEFAULT_COLUMN_VISIBILITY, sortableKeys } from "./constants";
import { useAuthContext } from "providers/Auth";

const query = "/portal/v1/applications";

export interface ApplicationFilters {
  status?: ApplicationStatus[];
  properties?: { id: string; name: string }[];
  cities?: string[];
  units?: { id: string; name: string }[];
}

// eslint-disable-next-line react-refresh/only-export-components
export enum ActiveTab {
  All = "All",
  Pending = "Pending",
  Started = "Started",
  Invited = "Invited",
  Approved = "Approved",
  ApprovedConditionally = "ApprovedConditionally",
  Declined = "Declined",
  Canceled = "Canceled",
  Submitted = "Submitted"
}

const DEFAULT_PER_PAGE = window.localStorage.getItem("per_page") || "10";

export const ApplicationsTableView = ({
  propertyId,
  unitId
}: {
  propertyId?: number;
  unitId?: number;
}) => {
  const { isMobile } = useMedia();
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [selectedRentersRow, setSelectedRentersRow] =
    useState<RowSelectionState>({});

  const [sorting, setSorting] = useState<SortingState>([]);
  const { auth, refreshAuthUserDashboardState } = useAuthContext();

  const clearSelectedRows = () => {
    setSelectedRentersRow({});
  };
  const [cancelModalId, setCancelModalId] = useState<string | undefined>();
  const [uncancelModalId, setUncancelModalId] = useState<string | undefined>();
  const [moveModalId, setMoveModalId] = useState<string | undefined>();
  const [isShowModal, setIsShowModal] = useState(false);
  const [searchParams, setSearchParams] = useSearchParamsState(
    {
      application: "",
      page: "1",
      tab: "",
      per_page: DEFAULT_PER_PAGE
    },
    ["application", "page", "tab", "per_page"]
  );
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<ApplicationFilters>();
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- it's necessary
  const activeTab = (searchParams.tab as ActiveTab) || ActiveTab.Pending;
  const [isFirstTotalPendingUpdate, setIsFirstTotalPendingUpdate] =
    useState(true);
  const [refundModalId, setRefundModalId] = useState<string | undefined>();

  const [debouncedParams] = useDebouncedValue(searchParams.application, 500);

  useEffect(() => {
    setSearchParams({
      ...searchParams,
      tab: searchParams.tab ? searchParams.tab : ActiveTab.Pending,
      page: searchParams.page ? searchParams.page : "1"
    });
  }, [filters]);
  const dataQuery = useQuery<Application>({
    queryKey: ["APPLICATION", cancelModalId],
    queryFn: () => addressApi.getApplicationById(cancelModalId || ""),
    enabled: !!cancelModalId
  });

  const { applicants } = dataQuery.data || {};

  const updateColumnVisibility = useMutation({
    mutationKey: ["updateColumnVisibility"],
    mutationFn: (columnsVisibility: any) =>
      instance.patch(`/portal/v1/users/${auth?.id}`, {
        columns_visibility: columnsVisibility
      })
  });

  const [columnsVisibility, setColumnsVisibility] = useState(
    auth?.columns_visibility?.[activeTab.toLowerCase()] ||
      DEFAULT_COLUMN_VISIBILITY
  );
  const [totalColumnsVisibility, setTotalColumnsVisibility] = useState(
    auth?.columns_visibility || {}
  );

  useEffect(() => {
    setColumnsVisibility(
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      totalColumnsVisibility[activeTab.toLowerCase()] ||
        DEFAULT_COLUMN_VISIBILITY
    );
  }, [activeTab]);

  const handleColumnToggle = async (id: string, checked: boolean) => {
    const updateColumnsVisibility = columnsVisibility.map((col) =>
      col.id === id ? { ...col, checked } : col
    );
    setColumnsVisibility(updateColumnsVisibility);

    const updatedColumnsVisibility = {
      ...totalColumnsVisibility,
      [activeTab.toLowerCase()]: updateColumnsVisibility
    };
    setTotalColumnsVisibility(updatedColumnsVisibility);

    try {
      await updateColumnVisibility
        .mutateAsync(updatedColumnsVisibility)
        .then(() => {
          refreshAuthUserDashboardState();
        });
    } catch (error) {
      toast.error(
        `Failed to update column visibility: ${getErrorMessage(error)}`
      );
    }
  };
  const getApplications = async (
    status?: ApplicationStatus[],
    perPage?: number
  ) => {
    const sorts = sorting.reduce(
      (acc, el, idx) => {
        const key = sortableKeys.filter((key) => el.id.includes(key))[0];

        if (key)
          acc.push({
            [key]: sorting[idx]?.desc ? "desc" : "asc"
          });

        return acc;
      },
      [] as {
        [key: string]: "asc" | "desc";
      }[]
    );

    return instance
      .get(
        query +
          "?" +
          queryString.stringify(
            {
              per_page: perPage || searchParams.per_page || DEFAULT_PER_PAGE,
              page: searchParams.page || "1",
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              search: debouncedParams?.trim() || undefined,
              status,
              property_ids: filters?.properties?.map((p) => p.id),
              unit_ids: filters?.units?.map((u) => u.id),
              cities: filters?.cities,
              property_id: propertyId,
              unit_id: unitId,
              sorts
            },
            {
              arrayFormat: "brackets"
            }
          )
      )
      .then(({ data }: { data: PaginatedResponse<Application> }) => data);
  };

  const buildQuery = (status: ApplicationStatus) => {
    return {
      queryKey: [
        sorting[0]?.desc,
        sorting[0]?.id,
        query + "__total",
        {
          page: 1,
          per_page: 1,
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          search: debouncedParams?.trim() || undefined,
          status: status,
          property_ids: filters?.properties?.map((p) => p.id),
          unit_ids: filters?.units?.map((u) => u.id),
          cities: filters?.cities,
          property_id: propertyId,
          unit_id: unitId
        }
      ],
      queryFn: () => getApplications([status], 1).then((d) => d.total_count)
    };
  };

  const [{ data: totalPendingCount }, { data: totalSubmittedCount }] =
    useQueries({
      queries: [
        buildQuery(ApplicationStatus.Submitted),
        buildQuery(ApplicationStatus.PartiallySubmitted)
      ]
    });
  useEffect(() => {
    if (
      totalPendingCount &&
      totalPendingCount > 0 &&
      isFirstTotalPendingUpdate &&
      !searchParams.tab
    ) {
      setSearchParams({ ...searchParams, tab: ActiveTab.Pending });
      setIsFirstTotalPendingUpdate(false);
    }
  }, [totalPendingCount]);

  const status = useMemo(() => {
    const statusMap = {
      Pending: ApplicationStatus.Submitted,
      Started: ApplicationStatus.Started,
      Invited: ApplicationStatus.Pending,
      Approved: ApplicationStatus.Approved,
      ApprovedConditionally: ApplicationStatus.ConditionalApproved,
      Declined: ApplicationStatus.Rejected,
      Canceled: ApplicationStatus.Canceled,
      Submitted: ApplicationStatus.PartiallySubmitted
    };
    return statusMap[activeTab] ? [statusMap[activeTab]] : [];
  }, [activeTab]);

  const { data: applications, isFetching } = useQuery({
    queryKey: [
      "APPLICANTS_TABLE",
      query,
      {
        applications: debouncedParams,
        per_page: searchParams.per_page || DEFAULT_PER_PAGE,
        page: searchParams.page || "1",
        status,
        property_ids: filters?.properties?.map((p) => p.id),
        unit_ids: filters?.units?.map((u) => u.id),
        cities: filters?.cities,
        property_id: propertyId,
        unit_id: unitId,
        sorting
      }
    ],
    queryFn: () => getApplications(status),
    placeholderData: keepPreviousData
  });

  const tableData = useMemo(() => {
    return applications?.items;
  }, [applications?.items]);

  return (
    <>
      <div className="space-y-4">
        <div className="flex w-full justify-between flex-wrap md:flex-nowrap gap-2 md:max-w-[calc(100vw-124px)]">
          <div className="flex w-full items-center gap-2 ">
            <Input
              id="search"
              autoComplete="off"
              placeholder="Search applications"
              value={searchParams.application}
              onChange={(e) =>
                setSearchParams({
                  ...searchParams,
                  page: "1",
                  application: e.target.value
                })
              }
              size={InputSize.SM}
              wrapperClassName="w-full md:w-auto"
              className="!mb-0 md:max-w-[271px]"
            />
          </div>

          {propertyId || unitId ? null : (
            <div className="md:flex w-full justify-end hidden">
              <Button
                variant={ButtonVariant.PRIMARY}
                className="w-full md:w-auto"
                size={ButtonSize.SM}
                Icon={Plus}
                onClick={() => setIsShowModal(true)}
              >
                Create application
              </Button>
            </div>
          )}
        </div>

        <div className="flex gap-2 h-10">
          <Tabs
            tabs={[
              ActiveTab.All,
              ActiveTab.Pending,
              ActiveTab.Submitted,
              ActiveTab.Started,
              ActiveTab.Invited,
              ActiveTab.Approved,
              ActiveTab.ApprovedConditionally,
              ActiveTab.Declined,
              ActiveTab.Canceled
            ]}
            displayMap={{
              All: "All",
              Pending: (
                <>
                  Pending decision
                  {(totalPendingCount || 0) > 0 ? (
                    <CountNotification totalCount={totalPendingCount} />
                  ) : (
                    ""
                  )}
                </>
              ),
              Started: "In progress",
              Approved: "Approved",
              ApprovedConditionally: "Approved conditionally",
              Invited: "Invited",
              Declined: "Declined",
              Canceled: "Canceled",
              Submitted: (
                <>
                  Submitted
                  {(totalSubmittedCount || 0) > 0 ? (
                    <CountNotification totalCount={totalSubmittedCount} />
                  ) : (
                    ""
                  )}
                </>
              )
            }}
            selected={activeTab}
            setActiveTab={(t: ActiveTab) => {
              setSearchParams({
                tab: t,
                application: searchParams.application,
                page: "1"
              });
            }}
          />
          <Show if={!unitId}>
            <div className="relative">
              <IconButton
                size={isMobile ? IconButtonSize.BASE : IconButtonSize.SM}
                variant={IconButtonVariant.BASE}
                Icon={FilterLines}
                onClick={() => setShowFilters(true)}
              />
              {Object.values(filters ?? {}).some((f) => f?.length) && (
                <div className="p-1 rounded-full bg-white absolute bottom-[5px] right-[4px]">
                  <div className=" size-2 z-20 bg-primary-500 rounded-full" />
                </div>
              )}
            </div>
          </Show>

          <div className="relative">
            <ColumnsMenu
              activeTab={activeTab}
              isMobile={isMobile}
              columnsData={columnsVisibility}
              onColumnToggle={handleColumnToggle}
            />
          </div>
        </div>

        <Show if={Object.values(filters ?? {}).some((f) => f?.length)}>
          <div className="flex flex-wrap gap-2">
            {filters?.properties?.map((property) => (
              <FilterBadge
                key={property.id}
                filter={property.name}
                onRemove={() =>
                  setFilters((prev) => ({
                    ...prev,
                    properties: prev?.properties?.filter(
                      (p) => p.id !== property.id
                    )
                  }))
                }
              />
            ))}

            {filters?.cities?.map((city) => (
              <FilterBadge
                key={city}
                filter={city}
                onRemove={() =>
                  setFilters((prev) => ({
                    ...prev,
                    cities: prev?.cities?.filter((c) => c !== city)
                  }))
                }
              />
            ))}

            {filters?.units?.map((unit) => (
              <FilterBadge
                key={unit.id}
                filter={unit.name}
                onRemove={() =>
                  setFilters((prev) => ({
                    ...prev,
                    units: prev?.units?.filter((u) => u.id !== unit.id)
                  }))
                }
              />
            ))}
          </div>
        </Show>

        {Object.values(selectedRentersRow).filter(Boolean).length ? (
          <div className="shadow-lg bg-white px-4 py-2 rounded-lg border border-alpha-100 z-[11] absolute right-10 bottom-24 animate-appear flex justify-end items-center gap-2">
            <p className="mr-4 text-sm font-medium text-content-1000 truncate">
              {Object.values(selectedRentersRow).filter(Boolean).length}{" "}
              applications selected
            </p>
            <Button
              size={ButtonSize.SM}
              onClick={() => {
                setShowMergeModal(true);
              }}
              variant={ButtonVariant.PRIMARY}
              disabled={
                Object.values(selectedRentersRow).filter(Boolean).length < 2
              }
            >
              Merge applications
            </Button>
            <IconButton
              size={IconButtonSize.SM}
              Icon={XClose}
              onClick={clearSelectedRows}
            />
          </div>
        ) : null}

        <TableApplications
          setSorting={setSorting}
          sorting={sorting}
          activeTab={activeTab}
          sortableKeys={sortableKeys}
          selectedRentersRow={selectedRentersRow as Record<string, boolean>}
          setSelectedRentersRow={setSelectedRentersRow}
          applications={tableData ?? []}
          onInviteClick={() => {
            setIsShowModal(true);
          }}
          isLoading={isFetching}
          onCancelClick={(id) => {
            setCancelModalId(id);
          }}
          onUncancelClick={(id) => {
            setUncancelModalId(id);
          }}
          onRefundClick={(id) => setRefundModalId(id)}
          onMoveClick={(id) => setMoveModalId(id)}
          hasSearchParams={!!debouncedParams}
          columnsVisibility={columnsVisibility}
          invalidateQueries={[
            "APPLICANTS_TABLE",
            query,
            {
              applications: debouncedParams,
              per_page: searchParams.per_page || DEFAULT_PER_PAGE,
              page: searchParams.page || "1",
              status,
              property_id: propertyId,
              unit_id: unitId,
              sorting
            }
          ]}
        />

        <div className="ml-auto flex w-full max-w-[calc(100vw-106px)]">
          <Pagination
            className="justify-end"
            total={applications?.page_count as number}
            current={Number(searchParams.page)}
            onChange={(e) =>
              setSearchParams({ ...searchParams, page: String(e) })
            }
          />
        </div>
      </div>
      {showMergeModal ? (
        <MergeApplicantsModal
          isOpen={showMergeModal}
          onClose={() => setShowMergeModal(false)}
          selectedApplicants={Object.keys(selectedRentersRow)}
        />
      ) : null}
      {isShowModal && (
        <InviteAndCreateModal
          open={isShowModal}
          setOpen={() => setIsShowModal(false)}
          unitId={unitId}
          propertyId={propertyId}
          onInviteRefreshKey={[
            "APPLICANTS_TABLE",
            query,
            {
              applications: debouncedParams,
              per_page: searchParams.per_page || DEFAULT_PER_PAGE,
              page: searchParams.page || "1",
              status,
              property_ids: filters?.properties?.map((p) => p.id),
              unit_ids: filters?.units?.map((u) => u.id),
              cities: filters?.cities,
              property_id: propertyId,
              unit_id: unitId,
              sorting
            }
          ]}
        />
      )}
      <Show if={Boolean(cancelModalId)}>
        <CancelOrRefundModal
          applicants={applicants}
          applicationId={cancelModalId ?? ""}
          onClose={() => setCancelModalId(undefined)}
        />
      </Show>
      <Show if={Boolean(uncancelModalId)}>
        <UncancelModal
          applicationId={uncancelModalId ?? ""}
          onClose={() => setUncancelModalId(undefined)}
        />
      </Show>
      <Show if={Boolean(moveModalId)}>
        <MoveModal
          applicationId={moveModalId ?? ""}
          onClose={() => setMoveModalId(undefined)}
        />
      </Show>
      <Show if={Boolean(refundModalId)}>
        <RefundModal
          queryKey={[
            "APPLICANTS_TABLE",
            query,
            {
              applications: debouncedParams,
              per_page: searchParams.per_page || DEFAULT_PER_PAGE,
              page: searchParams.page || "1",
              status,
              property_ids: filters?.properties?.map((p) => p.id),
              unit_ids: filters?.units?.map((u) => u.id),
              cities: filters?.cities,
              property_id: propertyId,
              unit_id: unitId,
              sorting
            }
          ]}
          applicationId={refundModalId ?? ""}
          onClose={() => setRefundModalId(undefined)}
        />
      </Show>
      <FiltersDrawer
        unitId={unitId}
        propertyId={propertyId}
        filters={filters}
        setFilters={setFilters}
        showFilters={showFilters}
        setShowFilters={setShowFilters}
      />
    </>
  );
};
